import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Bug, Sparkles, MessageCircle, RefreshCw, Search,
  ChevronRight, Clock, CheckCircle2, AlertCircle, Archive,
  Send, User, Mail, ExternalLink, Paperclip, X, FileText, Image as ImageIcon, File,
  Megaphone
} from "lucide-react";
import UpdatesSection from "./UpdatesSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MESSAGE_TYPES = [
  { id: "bug", label: "Bug Reports", icon: Bug, color: "text-red-500", bgColor: "bg-red-50" },
  { id: "feature", label: "Feature Requests", icon: Sparkles, color: "text-purple-500", bgColor: "bg-purple-50" },
  { id: "chat", label: "Quick Chat", icon: MessageCircle, color: "text-blue-500", bgColor: "bg-blue-50" }
];

const MESSAGE_STATUSES = [
  { id: "new", label: "New", color: "bg-blue-500" },
  { id: "in_progress", label: "In Progress", color: "bg-yellow-500" },
  { id: "resolved", label: "Resolved", color: "bg-green-500" },
  { id: "closed", label: "Closed", color: "bg-gray-500" },
  { id: "archived", label: "Archived", color: "bg-gray-400" }
];

const MESSAGE_PRIORITIES = [
  { id: "low", label: "Low", color: "bg-gray-400" },
  { id: "medium", label: "Medium", color: "bg-yellow-500" },
  { id: "high", label: "High", color: "bg-orange-500" },
  { id: "critical", label: "Critical", color: "bg-red-500" }
];

export default function AdminMessagesSection() {
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({ bug: 0, feature: 0, chat: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Main section tab: messages or updates
  const [mainSection, setMainSection] = useState("messages");
  
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploading(true);
    const token = localStorage.getItem("session_token");
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        if (selectedMessage?.message_id) {
          formData.append("feedback_id", selectedMessage.message_id);
        }
        
        const res = await fetch(`${API}/feedback/upload-attachment`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });
        
        if (res.ok) {
          const data = await res.json();
          setAttachments(prev => [...prev, data.attachment]);
          toast.success(`Uploaded: ${file.name}`);
        } else {
          toast.error(`Failed to upload: ${file.name}`);
        }
      } catch (err) {
        toast.error(`Error uploading: ${file.name}`);
      }
    }
    
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Remove attachment
  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.attachment_id !== attachmentId));
  };

  // Get file icon based on type
  const getFileIcon = (contentType) => {
    if (contentType?.startsWith("image/")) return ImageIcon;
    if (contentType?.includes("pdf")) return FileText;
    return File;
  };

  // Fetch messages
  const fetchMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem("session_token");
      const typeParam = activeType !== "all" ? `?message_type=${activeType}` : "";
      const res = await fetch(`${API}/admin/messages${typeParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setCounts(data.counts || { bug: 0, feature: 0, chat: 0, total: 0 });
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
    } finally {
      setLoading(false);
    }
  }, [activeType]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Update message status/priority
  const updateMessage = async (messageId, updates) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/messages/${messageId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        toast.success("Message updated");
        fetchMessages();
      }
    } catch (err) {
      toast.error("Failed to update message");
    }
  };

  // Send reply
  const sendReply = async () => {
    if (!replyContent.trim() || !selectedMessage) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem("session_token");
      
      // If this is a feedback message, use the feedback reply endpoint
      if (selectedMessage.is_feedback) {
        const res = await fetch(`${API}/admin/feedback/${selectedMessage.message_id}/reply`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ reply: replyContent })
        });
        
        if (res.ok) {
          toast.success("Reply sent to user");
          setReplyContent("");
          setSelectedMessage(prev => ({ ...prev, admin_reply: replyContent, reply_at: new Date().toISOString() }));
          fetchMessages();
        }
      } else {
        // Portal message reply
        const res = await fetch(`${API}/admin/messages`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            parent_id: selectedMessage.message_id,
            message_type: selectedMessage.message_type,
            content: replyContent,
            user_id: selectedMessage.user_id,
            portal_id: selectedMessage.portal_id
          })
        });
        
        if (res.ok) {
          toast.success("Reply sent");
          setReplyContent("");
          fetchMessages();
        }
      }
    } catch (err) {
      toast.error("Failed to send reply");
    } finally {
      setSubmitting(false);
    }
  };

  // Get type config
  const getTypeConfig = (typeId) => MESSAGE_TYPES.find(t => t.id === typeId) || MESSAGE_TYPES[2];
  const getStatusConfig = (statusId) => MESSAGE_STATUSES.find(s => s.id === statusId) || MESSAGE_STATUSES[0];
  const getPriorityConfig = (priorityId) => MESSAGE_PRIORITIES.find(p => p.id === priorityId) || MESSAGE_PRIORITIES[1];

  // Filter messages by search, source, and status
  const filteredMessages = messages.filter(msg => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        (msg.title || "").toLowerCase().includes(query) ||
        (msg.content || "").toLowerCase().includes(query) ||
        (msg.user_name || "").toLowerCase().includes(query) ||
        (msg.user_email || "").toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }
    
    // Source filter
    if (sourceFilter !== "all") {
      if (sourceFilter === "help_widget" && msg.source !== "help_widget") return false;
      if (sourceFilter === "pilot_chat" && msg.source !== "pilot_chat") return false;
      if (sourceFilter === "feedback" && !msg.is_feedback) return false;
    }
    
    // Status filter
    if (statusFilter !== "all" && msg.status !== statusFilter) return false;
    
    return true;
  });

  if (loading) {
    return (
      <div className="p-8 flex justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Main Section Header with Tabs */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-xl md:text-2xl font-bold">Messages & Updates</h1>
          <p className="text-sm text-gray-500">Manage messages and send updates to users</p>
        </div>
      </div>

      {/* Main Section Tabs */}
      <Tabs value={mainSection} onValueChange={setMainSection} className="mb-6">
        <TabsList className="grid w-full max-w-xs grid-cols-2">
          <TabsTrigger value="messages" className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            Messages
          </TabsTrigger>
          <TabsTrigger value="updates" className="flex items-center gap-2">
            <Megaphone className="w-4 h-4" />
            Updates
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Updates Tab Content */}
      {mainSection === "updates" && (
        <UpdatesSection />
      )}

      {/* Messages Tab Content */}
      {mainSection === "messages" && (
      <>
      {/* Messages Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-lg font-semibold">Inbox</h2>
          <p className="text-sm text-gray-500">Bug reports, feature requests, and chat from portals</p>
        </div>
        
        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Refresh Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchMessages()}
            className="flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
          
          {/* Source Filter */}
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Source" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="help_widget">Help Widget</SelectItem>
              <SelectItem value="pilot_chat">Pilot Chat</SelectItem>
              <SelectItem value="feedback">Feedback</SelectItem>
            </SelectContent>
          </Select>
          
          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {MESSAGE_STATUSES.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Search */}
          <div className="relative w-full sm:w-48">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="pl-10"
            />
          </div>
        </div>
      </div>

      {/* Type Tabs */}
      <Tabs value={activeType} onValueChange={setActiveType} className="space-y-6">
        <TabsList className="bg-white border p-1 rounded-xl w-full sm:w-auto overflow-x-auto">
          <TabsTrigger value="all" className="rounded-lg whitespace-nowrap">
            All ({counts.total})
          </TabsTrigger>
          {MESSAGE_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <TabsTrigger key={type.id} value={type.id} className="rounded-lg whitespace-nowrap">
                <Icon className={`w-4 h-4 mr-1.5 ${type.color}`} />
                {type.label} ({counts[type.id] || 0})
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Messages List */}
        <TabsContent value={activeType} className="m-0">
          {filteredMessages.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="font-medium">No messages</p>
                <p className="text-sm text-gray-500 mt-1">
                  {activeType === "all" 
                    ? "Messages from portal members will appear here"
                    : `No ${activeType} messages yet`}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredMessages.map((msg) => {
                const typeConfig = getTypeConfig(msg.message_type);
                const statusConfig = getStatusConfig(msg.status);
                const priorityConfig = getPriorityConfig(msg.priority);
                const TypeIcon = typeConfig.icon;
                
                return (
                  <Card 
                    key={msg.message_id} 
                    className={`cursor-pointer hover:shadow-md transition-shadow ${typeConfig.bgColor}`}
                    onClick={() => { setSelectedMessage(msg); setShowDetailDialog(true); }}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`w-10 h-10 rounded-full ${typeConfig.bgColor} ${typeConfig.color} flex items-center justify-center flex-shrink-0 border`}>
                            <TypeIcon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-medium text-gray-900 truncate">
                                {msg.title || msg.content?.slice(0, 50) || "No title"}
                              </h4>
                              <Badge className={`${statusConfig.color} text-white text-xs`}>
                                {statusConfig.label}
                              </Badge>
                              <Badge className={`${priorityConfig.color} text-white text-xs`}>
                                {priorityConfig.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{msg.content}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {msg.user_name || msg.user_email || "Anonymous"}
                              </span>
                              {msg.user_business && (
                                <span className="text-gray-400">• {msg.user_business}</span>
                              )}
                              {msg.source && (
                                <Badge variant="outline" className="text-xs">
                                  {msg.source === "help_widget" ? "Help Widget" : 
                                   msg.source === "pilot_chat" ? "Pilot Chat" : 
                                   msg.portal_type ? `${msg.portal_type} Pilot` : msg.source}
                                </Badge>
                              )}
                              {msg.is_feedback && (
                                <Badge variant="outline" className="text-xs bg-purple-50 text-purple-600 border-purple-200">
                                  Feedback
                                </Badge>
                              )}
                              {msg.admin_reply && (
                                <Badge className="text-xs bg-green-500 text-white">
                                  Replied
                                </Badge>
                              )}
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(msg.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 hidden sm:block" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Message Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedMessage && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  {(() => {
                    const typeConfig = getTypeConfig(selectedMessage.message_type);
                    const TypeIcon = typeConfig.icon;
                    return <TypeIcon className={`w-5 h-5 ${typeConfig.color}`} />;
                  })()}
                  <DialogTitle>{selectedMessage.title || "Message"}</DialogTitle>
                </div>
                <DialogDescription className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {selectedMessage.user_name || selectedMessage.user_email || "Anonymous"}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* User Info */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-500" />
                    </div>
                    <div>
                      <p className="font-medium">{selectedMessage.user_name || "Anonymous"}</p>
                      <p className="text-sm text-gray-500">{selectedMessage.user_email}</p>
                      {selectedMessage.user_business && (
                        <p className="text-xs text-gray-400">{selectedMessage.user_business}</p>
                      )}
                    </div>
                  </div>
                  {selectedMessage.source && (
                    <div className="mt-2 flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        Source: {selectedMessage.source === "help_widget" ? "Help Widget" : 
                                selectedMessage.source === "pilot_chat" ? "Pilot Chat" : selectedMessage.source}
                      </Badge>
                      {selectedMessage.portal_type && (
                        <Badge variant="outline" className="text-xs">
                          {selectedMessage.portal_type} Pilot
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{selectedMessage.content}</p>
                </div>
                
                {/* Attachments */}
                {(selectedMessage.attachment_url || selectedMessage.screenshot_url || selectedMessage.attachments?.length > 0) && (
                  <div className="border rounded-lg p-3">
                    <p className="text-sm font-medium mb-2">Attachments</p>
                    <div className="flex gap-2 flex-wrap">
                      {selectedMessage.attachment_url && (
                        <a 
                          href={selectedMessage.attachment_url.startsWith('http') ? selectedMessage.attachment_url : `${API}${selectedMessage.attachment_url}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-600 rounded text-sm hover:bg-blue-100"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View Attachment
                        </a>
                      )}
                      {selectedMessage.screenshot_url && (
                        <a 
                          href={selectedMessage.screenshot_url.startsWith('http') ? selectedMessage.screenshot_url : `${API}${selectedMessage.screenshot_url}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-600 rounded text-sm hover:bg-blue-100"
                        >
                          <ImageIcon className="w-3 h-3" />
                          View Screenshot
                        </a>
                      )}
                      {/* Multiple attachments array */}
                      {selectedMessage.attachments?.map((att, idx) => {
                        const FileIcon = getFileIcon(att.content_type);
                        const url = att.url?.startsWith('http') ? att.url : `${API}${att.url}`;
                        return (
                          <a 
                            key={att.attachment_id || idx}
                            href={url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1 bg-gray-50 text-gray-700 rounded text-sm hover:bg-gray-100 border"
                          >
                            <FileIcon className="w-3 h-3" />
                            {att.original_name || `Attachment ${idx + 1}`}
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Existing Admin Reply */}
                {selectedMessage.admin_reply && (
                  <div className="border-l-4 border-green-500 pl-4 py-2 bg-green-50 rounded-r">
                    <p className="text-xs font-medium text-green-700 mb-1">Admin Reply</p>
                    <p className="text-sm text-gray-700">{selectedMessage.admin_reply}</p>
                    {selectedMessage.reply_at && (
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(selectedMessage.reply_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Meta Info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Status</p>
                    <Select
                      value={selectedMessage.status}
                      onValueChange={(val) => {
                        updateMessage(selectedMessage.message_id, { status: val });
                        setSelectedMessage(prev => ({ ...prev, status: val }));
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MESSAGE_STATUSES.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <p className="text-gray-500">Priority</p>
                    <Select
                      value={selectedMessage.priority}
                      onValueChange={(val) => {
                        updateMessage(selectedMessage.message_id, { priority: val });
                        setSelectedMessage(prev => ({ ...prev, priority: val }));
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MESSAGE_PRIORITIES.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                {/* Page URL if available */}
                {selectedMessage.page_url && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <ExternalLink className="w-4 h-4" />
                    <a href={selectedMessage.page_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate">
                      {selectedMessage.page_url}
                    </a>
                  </div>
                )}
                
                {/* Reply Section */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Send Reply</p>
                  <Textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                  />
                  
                  {/* Attachments Preview */}
                  {attachments.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {attachments.map(att => {
                        const FileIcon = getFileIcon(att.content_type);
                        return (
                          <div 
                            key={att.attachment_id} 
                            className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded text-sm"
                          >
                            <FileIcon className="w-4 h-4 text-gray-500" />
                            <span className="truncate max-w-[150px]">{att.original_name}</span>
                            <button 
                              onClick={() => removeAttachment(att.attachment_id)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* File Upload Button */}
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.txt,.csv"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Paperclip className="w-4 h-4 mr-1" />
                      )}
                      {uploading ? "Uploading..." : "Attach File"}
                    </Button>
                    <span className="text-xs text-gray-500">
                      Images, PDFs, Documents (max 10MB)
                    </span>
                  </div>
                </div>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
                  Close
                </Button>
                <Button onClick={sendReply} disabled={submitting || !replyContent.trim()}>
                  {submitting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Reply
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
}
